<template>
  <section class="record-form">
    <AppPageTitleBar
      :title="pageTitle"
      :back-to="step === 1 ? returnRoute : null"
      :on-back="step === 2 ? returnToEntry : null"
      back-label="Natrag"
    />

    <v-progress-linear v-if="isLoading" indeterminate />
    <v-alert v-else-if="loadError" type="error" variant="tonal">{{ loadError }}</v-alert>

    <v-form
      v-else
      ref="recordFormRef"
      class="form form--with-sticky-actions page-stack grid-stack grid-gap-page-section"
      :inert="isSaving"
      @submit.prevent="step === 1 ? goToReviewAsync() : createRecordAsync()"
    >
      <v-alert v-if="photoUploadNeedsReview" type="warning" variant="tonal">
        {{ isReturnForm ? 'Nije moguće potvrditi jesu li fotografije prenesene. Ponovno učitajte zapisnik i provjerite popis fotografija prije novog pokušaja.' : 'Nije moguće potvrditi jesu li fotografije prenesene. Ponovno učitajte ovaj Draft i provjerite popis fotografija prije novog pokušaja.' }}
      </v-alert>
      <RecordVehicleSection
        :vehicle-id="pickupValues.vehicleId"
        :vehicle="selectedVehicle"
        :vehicles="vehicleOptions"
        :vehicle-error="vehicleError"
        :readonly="pickupReadonly"
        @update:vehicle-id="setVehicleId"
      />
      <RecordEquipmentSection v-model:equipment="pickupValues.equipment" :readonly="pickupReadonly" />
      <RecordUserSection :values="pickupValues" :readonly="pickupReadonly" @change="setPickupValue" />
      <RecordUsageSection :values="pickupValues" :readonly="pickupReadonly" :show-working-hours="!isReturnForm" @change="setPickupValue" />
      <RecordPickupConditionSection :values="pickupValues" :readonly="pickupReadonly" @change="setPickupValue" />
      <RecordReturnConditionSection
        v-if="isReturnForm"
        :values="returnValues"
        :driven-duration="drivenDuration"
        :readonly="step === 2"
        @change="setReturnValue"
      />
      <RecordPhotosSection
        :saved-photos="stagePhotos"
        :pending-files="pendingFiles"
        :readonly="step === 2"
        :disabled="isSaving || photoUploadNeedsReview"
        @add-files="addSelectedFiles"
        @remove-saved="removeSavedPhoto"
        @remove-pending="removePendingFile"
        @download="downloadPhotoAsync"
      />

      <section v-if="step === 2" class="page-panel">
        <header class="page-panel__header"><h3 class="panel-title">Potpisi</h3></header>
        <div class="page-panel__content grid-stack grid-row-gap-18">
          <v-checkbox
            v-model="termsAccepted"
            class="checkbox"
            density="compact"
            hide-details
            :error="Boolean(termsError)"
          >
            <template #label>
              Pročitao sam i prihvatio opće uvjete otisnute na poleđini te izjavljujem da sam suglasan da su oni sastavni dio&nbsp;
              <router-link class="record-form__terms-link" :to="termsRoute" @click.stop>zapisnika</router-link>.
            </template>
          </v-checkbox>
          <p v-if="termsError" class="record-form__error" role="alert">{{ termsError }}</p>

          <div class="grid grid-column-gap-16 grid-row-gap-18">
            <div class="col-12 col-sm-6">
              <label class="record-form__signature-label control-label">Potpis korisnika pri {{ isReturnForm ? 'vraćanju' : 'preuzimanju' }} vozila <span class="required">*</span></label>
              <SignaturePad v-model="userSignature" class="record-form__signature-pad" :error-message="userSignatureError" />
              <div class="record-form__signature-actions">
                <span class="text-hint">Kliknite i povucite za potpis</span>
                <button type="button" :disabled="isSaving" @click="userSignature = ''">Obriši</button>
              </div>
            </div>
            <div class="col-12 col-sm-6">
              <label class="record-form__signature-label control-label">Potpis Porsche Croatia pri {{ isReturnForm ? 'vraćanju' : 'preuzimanju' }} vozila <span class="required">*</span></label>
              <SignaturePad v-model="companySignature" class="record-form__signature-pad" :error-message="companySignatureError" />
              <div class="record-form__signature-actions">
                <span class="text-hint">Kliknite i povucite za potpis</span>
                <button type="button" :disabled="isSaving" @click="companySignature = ''">Obriši</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <aside v-if="step === 2" class="notice">
        <strong>Zaštita osobnih podataka</strong><br>
        Osobne podatke obrađujemo isključivo u skladu s važećim propisima o zaštiti podataka. Vaši osobni podaci (ime, prezime, adresa, broj vozačke dozvole, broj telefona) navedeni u ovom obrascu nužni su za ispunjenje zakonskih obveza nastalih uslijed/za slučaj prometnih ili komunalnih prekršaja. Ukoliko nastupi prometni/komunalni prekršaj dodatno ćemo od Vas zatražiti i druge osobne podatke (OIB, prebivalište, datum rođenja). Svi navedeni podaci će se obrađivati samo u navedene svrhe i prosljeđivati nadležnim institucijama.
      </aside>

      <div class="submit-bar">
        <template v-if="step === 1">
          <v-btn class="button button--secondary" :disabled="isSaving || photoUploadNeedsReview" type="button" variant="outlined" @click="saveDraftAsync">PRIHVATI DRAFT</v-btn>
          <v-btn class="button button--dark" :disabled="isSaving || photoUploadNeedsReview" type="submit" variant="flat">{{ isReturnForm ? 'NASTAVI NA POTPISE' : 'POSTAVI POTPISE' }}</v-btn>
        </template>
        <template v-else>
          <span class="record-form__footer-note">
            {{ footerNote }}
          </span>
          <v-btn class="button button--dark" :loading="isSaving" :disabled="isSaving || photoUploadNeedsReview" type="submit" variant="flat">KREIRAJ ZAPISNIK</v-btn>
        </template>
      </div>
    </v-form>
  </section>
</template>

<script setup>
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'

import { getEventBrandName } from '../../helpers'
import AppPageTitleBar from '../../components/AppPageTitleBar.vue'
import RecordEquipmentSection from '../../components/records/RecordEquipmentSection.vue'
import RecordPhotosSection from '../../components/records/RecordPhotosSection.vue'
import RecordPickupConditionSection from '../../components/records/RecordPickupConditionSection.vue'
import RecordReturnConditionSection from '../../components/records/RecordReturnConditionSection.vue'
import RecordUsageSection from '../../components/records/RecordUsageSection.vue'
import RecordUserSection from '../../components/records/RecordUserSection.vue'
import RecordVehicleSection from '../../components/records/RecordVehicleSection.vue'
import SignaturePad from '../../components/SignaturePad.vue'
import { apiClient } from '../../services/apiClient'
import { showErrorToast, showRequestErrorToast, showSuccessToast } from '../../store/appStore'

dayjs.extend(utc)

const recordFields = [
  'company', 'companyAddress', 'fullName', 'address', 'email', 'phone',
  'driverLicenseNumber', 'driverLicenseCategory', 'driverLicenseIssuedBy',
  'driverLicenseValidUntil', 'usageFrom', 'usageTo', 'returnByTime',
  'returnLocation', 'specialArrangement',
]
const pickupFields = [
  ...recordFields, 'pickupMileageKm', 'pickupDate',
  'pickupTime', 'pickupBatteryPercent', 'pickupFuelLevel', 'pickupDamage',
]
const returnFields = [
  'returnMileageKm', 'returnDate', 'returnTime', 'returnBatteryPercent',
  'returnFuelLevel', 'returnDamage',
]

const maxPhotoSize = 10 * 1024 * 1024

const route = useRoute()
const router = useRouter()
const isReturnForm = ref(route.name === 'fleet-record-return-form')
const recordFormRef = ref(null)
const step = ref(1)
const vehicleError = ref('')
const termsAccepted = ref(false)
const userSignature = ref('')
const companySignature = ref('')
const termsError = ref('')
const userSignatureError = ref('')
const companySignatureError = ref('')
const recordId = ref(route.params.recordId ?? null)
const isLoading = ref(true)
const isSaving = ref(false)
const photoUploadNeedsReview = ref(false)
const loadError = ref('')
const vehicles = ref([])
const savedPhotos = ref([])
const pendingFiles = ref([])
const removedPhotoIds = ref([])
const pickupValues = reactive({
  vehicleId: null,
  equipment: [],
  ...Object.fromEntries(pickupFields.map((field) => [field, ''])),
})
const returnValues = reactive(Object.fromEntries(returnFields.map((field) => [field, ''])))

const selectedVehicle = computed(() => vehicles.value.find((vehicle) => vehicle.id === pickupValues.vehicleId) ?? null)
const vehicleOptions = computed(() => vehicles.value.map((vehicle) => ({
  title: `${getEventBrandName(vehicle.brandCode)} ${vehicle.name}, ${vehicle.plate}`,
  value: vehicle.id,
  plate: vehicle.plate,
})))

const stage = computed(() => isReturnForm.value ? 'return' : 'pickup')
const pickupReadonly = computed(() => isReturnForm.value || step.value === 2)
const stagePhotos = computed(() => savedPhotos.value.filter((photo) => photo.stage === stage.value))
const pageTitle = computed(() => {
  const title = isReturnForm.value ? 'Zapisnik o vraćanju' : 'Zapisnik o preuzimanju'
  return selectedVehicle.value
    ? `${title} — ${selectedVehicle.value.name} ${selectedVehicle.value.plate}`
    : title
})
const returnRoute = computed(() => route.query.from === 'calendar'
  ? { name: 'fleet-calendar', query: route.query.month ? { month: route.query.month } : {} }
  : { name: 'fleet-forms' })
const termsRoute = computed(() => ({ name: 'fleet-record-pickup-terms', query: { returnTo: route.fullPath } }))
const footerNote = computed(() => {
  const stored = isReturnForm.value ? 'spremljen' : 'pohranjen'
  return pickupValues.email
    ? `Zapisnik će biti ${stored} i kopija će biti automatski poslana na e-mail adresu korisnika.`
    : `Zapisnik će biti ${stored}.`
})
const drivenDuration = computed(() => {
  if (!pickupValues.pickupDate || !pickupValues.pickupTime || !returnValues.returnDate || !returnValues.returnTime) return { days: '', hours: '', dayUnit: 'dana', hourUnit: 'sati' }

  // Compare local clock values without applying daylight saving offsets.
  const pickupTime = dayjs.utc(`${pickupValues.pickupDate}T${pickupValues.pickupTime}`)
  const returnTime = dayjs.utc(`${returnValues.returnDate}T${returnValues.returnTime}`)
  const elapsedHours = Math.ceil((returnTime.valueOf() - pickupTime.valueOf()) / 3_600_000)

  if (!Number.isFinite(elapsedHours) || elapsedHours < 0) return { days: '', hours: '', dayUnit: 'dana', hourUnit: 'sati' }

  const days = Math.floor(elapsedHours / 24)
  const hours = elapsedHours % 24
  return {
    days: String(days),
    hours: String(hours),
    dayUnit: getCroatianPlural(days, 'dan', 'dana', 'dana'),
    hourUnit: getCroatianPlural(hours, 'sat', 'sata', 'sati'),
  }
})

onMounted(() => {
  const session = recordFormSession?.path === route.fullPath ? recordFormSession.state : null
  recordFormSession = null
  if (session) {
    recordId.value = session.recordId
    vehicles.value = session.vehicles
    savedPhotos.value = session.savedPhotos
    pendingFiles.value = session.pendingFiles
    removedPhotoIds.value = session.removedPhotoIds
    photoUploadNeedsReview.value = session.photoUploadNeedsReview
    Object.assign(pickupValues, session.pickupValues)
    Object.assign(returnValues, session.returnValues)
    isLoading.value = false
    step.value = session.step
    termsAccepted.value = session.termsAccepted
    userSignature.value = session.userSignature
    companySignature.value = session.companySignature
  } else {
    loadAsync(typeof route.query.reservationId === 'string' ? route.query.reservationId : null)
  }
})

onBeforeRouteLeave((to) => {
  if (isReturnForm.value && isSaving.value) return false
  if (to.name === 'fleet-record-pickup-terms') {
    rememberFormState()
  }
})

watch(termsAccepted, (accepted) => { if (accepted) termsError.value = '' })
watch(userSignature, (signature) => { if (signature) userSignatureError.value = '' })
watch(companySignature, (signature) => { if (signature) companySignatureError.value = '' })
watch(() => [route.name, route.params.recordId], ([name, id], [previousName]) => {
  if (!['fleet-record-pickup-create', 'fleet-record-pickup-form', 'fleet-record-return-form'].includes(name)) return
  if (isSaving.value && previousName === 'fleet-record-pickup-create' && name === 'fleet-record-pickup-form') return

  isReturnForm.value = name === 'fleet-record-return-form'
  recordId.value = id ?? null
  step.value = 1
  vehicleError.value = ''
  termsAccepted.value = false
  userSignature.value = ''
  companySignature.value = ''
  termsError.value = ''
  userSignatureError.value = ''
  companySignatureError.value = ''
  photoUploadNeedsReview.value = false
  vehicles.value = []
  savedPhotos.value = []
  pendingFiles.value = []
  removedPhotoIds.value = []
  Object.assign(pickupValues, { vehicleId: null, equipment: [], ...Object.fromEntries(pickupFields.map((field) => [field, ''])) })
  Object.assign(returnValues, Object.fromEntries(returnFields.map((field) => [field, ''])))
  loadAsync(typeof route.query.reservationId === 'string' ? route.query.reservationId : null)
})

function getCroatianPlural (count, one, few, many) {
  if (count % 100 >= 11 && count % 100 <= 14) return many
  if (count % 10 === 1) return one
  if (count % 10 >= 2 && count % 10 <= 4) return few
  return many
}

function rememberFormState () {
  recordFormSession = {
    path: route.fullPath,
    state: {
      recordId: recordId.value,
      vehicles: [...vehicles.value],
      savedPhotos: [...savedPhotos.value],
      pendingFiles: [...pendingFiles.value],
      removedPhotoIds: [...removedPhotoIds.value],
      photoUploadNeedsReview: photoUploadNeedsReview.value,
      pickupValues: { ...pickupValues, equipment: [...pickupValues.equipment] },
      returnValues: { ...returnValues },
      step: step.value,
      termsAccepted: termsAccepted.value,
      userSignature: userSignature.value,
      companySignature: companySignature.value,
    },
  }
}

function setPickupValue (field, value) {
  if (isSaving.value) return

  if (field === 'usageFrom' && (!pickupValues.pickupDate || pickupValues.pickupDate === pickupValues.usageFrom)) {
    pickupValues.pickupDate = value
  }

  pickupValues[field] = value
}

function setReturnValue (field, value) {
  if (isSaving.value) return
  returnValues[field] = value
}

async function loadAsync (reservationId = null) {
  isLoading.value = true
  loadError.value = ''

  try {
    if (recordId.value) {
      const [record, returnRecord] = await Promise.all([
        apiClient.getAsync(`/api/drivepark/records/${recordId.value}/pickup`),
        ...(isReturnForm.value ? [apiClient.getAsync(`/api/drivepark/records/${recordId.value}/return`)] : []),
      ])

      if (record.status !== (isReturnForm.value ? 'picked-up' : 'draft')) {
        loadError.value = isReturnForm.value
          ? 'Uređivati je moguće samo zapisnik sa statusom Preuzeto.'
          : 'Uređivati je moguće samo Draft zapisnik.'
        return
      }

      pickupValues.vehicleId = record.vehicleId
      pickupValues.equipment = [...record.equipment]
      for (const field of recordFields) {
        pickupValues[field] = record[field] ?? ''
      }
      Object.assign(pickupValues, {
        pickupMileageKm: record.pickupForm.mileageKm ?? '',
        pickupDate: record.pickupForm.date ?? '',
        pickupTime: record.pickupForm.time ?? '',
        pickupBatteryPercent: record.pickupForm.batteryPercent ?? '',
        pickupFuelLevel: record.pickupForm.fuelLevel ?? '',
        pickupDamage: record.pickupForm.damage ?? '',
      })
      savedPhotos.value = [
        ...record.pickupForm.photos,
        ...(isReturnForm.value ? returnRecord.returnForm.photos : []),
      ]
      vehicles.value = [{
        id: record.vehicleId,
        brandCode: record.vehicleBrandCode,
        name: record.vehicleName,
        plate: record.vehiclePlate,
        vin: record.vehicleVin,
        color: record.vehicleColor,
      }]
      if (isReturnForm.value) {
        Object.assign(returnValues, {
          returnMileageKm: returnRecord.returnForm.mileageKm ?? pickupValues.pickupMileageKm,
          returnDate: returnRecord.returnForm.date ?? '',
          returnTime: returnRecord.returnForm.time ?? '',
          returnBatteryPercent: returnRecord.returnForm.batteryPercent ?? '',
          returnFuelLevel: returnRecord.returnForm.fuelLevel ?? '',
          returnDamage: returnRecord.returnForm.damage ?? '',
        })
        return
      }
    } else if (reservationId) {
      const reservation = await apiClient.getAsync(`/api/drivepark/reservations/${reservationId}`)
      pickupValues.vehicleId = reservation.vehicleId
      pickupValues.company = reservation.company ?? ''
      pickupValues.fullName = reservation.fullName ?? ''
      pickupValues.usageFrom = reservation.startDate
      pickupValues.usageTo = reservation.endDate
      pickupValues.pickupDate = reservation.startDate

      if (reservation.contact) {
        const field = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(reservation.contact) ? 'email' : 'phone'
        pickupValues[field] = reservation.contact
      }
    }

    const availableVehicles = []
    let page = 1
    let totalResults = 0

    do {
      const response = await apiClient.getAsync('/api/vehicles', {
        queryParams: { page, perPage: 100 },
      })
      availableVehicles.push(...response.results)
      totalResults = response.pagination.totalResults
      page += 1
    } while ((page - 1) * 100 < totalResults)

    vehicles.value = [
      ...vehicles.value.filter((vehicle) => !availableVehicles.some(({ id }) => id === vehicle.id)),
      ...availableVehicles,
    ]

    if (!recordId.value && !vehicles.value.some(({ id }) => id === pickupValues.vehicleId)) {
      pickupValues.vehicleId = null
    }
  } catch (error) {
    console.error('Failed to load the record form.', error)
    loadError.value = isReturnForm.value
      ? 'Podatke za zapisnik o vraćanju nije bilo moguće učitati.'
      : 'Podatke za zapisnik nije bilo moguće učitati.'
  } finally {
    isLoading.value = false
  }
}

function setVehicleId (vehicleId) {
  if (isSaving.value) return

  pickupValues.vehicleId = vehicleId
  vehicleError.value = ''
}

function returnToEntry () {
  if (isSaving.value) return
  step.value = 1
}

function getInputError () {
  if (isReturnForm.value) {
    if (returnValues.returnMileageKm !== '' && (!Number.isInteger(Number(returnValues.returnMileageKm)) || Number(returnValues.returnMileageKm) < 0)) {
      return 'Km stanje mora biti cijeli broj od 0 naviše.'
    }
    if (returnValues.returnMileageKm !== '' && pickupValues.pickupMileageKm !== '' && Number(returnValues.returnMileageKm) < Number(pickupValues.pickupMileageKm)) {
      return 'Km stanje pri vraćanju ne može biti manje od km stanja pri preuzimanju.'
    }
    if (returnValues.returnDate && pickupValues.pickupDate && (
      returnValues.returnDate < pickupValues.pickupDate ||
      (returnValues.returnDate === pickupValues.pickupDate && returnValues.returnTime && pickupValues.pickupTime && returnValues.returnTime < pickupValues.pickupTime)
    )) {
      return 'Datum i vrijeme vraćanja ne mogu biti prije datuma i vremena preuzimanja.'
    }
    if (returnValues.returnBatteryPercent !== '' && (!Number.isInteger(Number(returnValues.returnBatteryPercent)) || Number(returnValues.returnBatteryPercent) < 0 || Number(returnValues.returnBatteryPercent) > 100)) {
      return 'Stanje baterije mora biti cijeli broj od 0 do 100.'
    }
    if (returnValues.returnFuelLevel.length > 50) {
      return 'Razina goriva može imati najviše 50 znakova.'
    }
    return ''
  }

  if (!pickupValues.vehicleId) {
    vehicleError.value = 'Obavezno polje'
    return 'Odaberite registarsku oznaku vozila.'
  }

  if (pickupValues.usageFrom && pickupValues.usageTo && pickupValues.usageTo < pickupValues.usageFrom) {
    return 'Datum završetka ne može biti prije datuma početka.'
  }

  if (pickupValues.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(pickupValues.email)) {
    return 'Unesite ispravnu e-mail adresu.'
  }

  for (const [field, limit] of Object.entries({
    company: 255,
    companyAddress: 255,
    fullName: 255,
    address: 255,
    email: 255,
    phone: 50,
    driverLicenseNumber: 50,
    driverLicenseCategory: 20,
    driverLicenseIssuedBy: 100,
    returnLocation: 255,
    specialArrangement: 500,
    pickupFuelLevel: 50,
  })) {
    if (String(pickupValues[field]).length > limit) {
      return `Polje je ograničeno na ${limit} znakova.`
    }
  }

  if (pickupValues.equipment.some((item) => item.trim().length > 255)) {
    return 'Naziv opreme može imati najviše 255 znakova.'
  }

  if (pickupValues.pickupMileageKm !== '' && (!Number.isInteger(Number(pickupValues.pickupMileageKm)) || Number(pickupValues.pickupMileageKm) < 0)) {
    return 'Km stanje mora biti cijeli broj od 0 naviše.'
  }

  if (pickupValues.pickupBatteryPercent !== '' && (!Number.isInteger(Number(pickupValues.pickupBatteryPercent)) || Number(pickupValues.pickupBatteryPercent) < 0 || Number(pickupValues.pickupBatteryPercent) > 100)) {
    return 'Stanje baterije mora biti cijeli broj od 0 do 100.'
  }

  return ''
}

function removeSavedPhoto (photoId) {
  removedPhotoIds.value.push(photoId)
  savedPhotos.value = savedPhotos.value.filter((photo) => photo.id !== photoId)
}

function getPickupPayload () {
  return {
    vehicleId: pickupValues.vehicleId,
    equipment: pickupValues.equipment.map((item) => item.trim()).filter(Boolean),
    ...Object.fromEntries(recordFields.map((field) => {
      const value = pickupValues[field]
      return [field, typeof value === 'string' ? value.trim() || null : value]
    })),
    pickupForm: {
      mileageKm: pickupValues.pickupMileageKm === '' ? null : Number(pickupValues.pickupMileageKm),
      date: pickupValues.pickupDate || null,
      time: pickupValues.pickupTime || null,
      batteryPercent: pickupValues.pickupBatteryPercent === '' ? null : Number(pickupValues.pickupBatteryPercent),
      fuelLevel: pickupValues.pickupFuelLevel.trim() || null,
      damage: pickupValues.pickupDamage.trim() || null,
    },
  }
}

function getReturnPayload () {
  return {
    returnForm: {
      mileageKm: returnValues.returnMileageKm === '' ? null : Number(returnValues.returnMileageKm),
      date: returnValues.returnDate || null,
      time: returnValues.returnTime || null,
      batteryPercent: returnValues.returnBatteryPercent === '' ? null : Number(returnValues.returnBatteryPercent),
      fuelLevel: returnValues.returnFuelLevel.trim() || null,
      damage: returnValues.returnDamage.trim() || null,
    },
  }
}

async function syncPhotosAsync () {
  for (const photoId of removedPhotoIds.value) {
    await apiClient.deleteAsync(`/api/drivepark/records/${recordId.value}/photos/${photoId}`)
    removedPhotoIds.value = removedPhotoIds.value.filter((id) => id !== photoId)
  }

  if (!pendingFiles.value.length) return

  const formData = new FormData()
  for (const file of pendingFiles.value) {
    formData.append('files', file)
  }

  let photos
  try {
    photos = await apiClient.postAsync(
      `/api/drivepark/records/${recordId.value}/photos`,
      formData,
      { queryParams: { stage: stage.value } }
    )
  } catch (error) {
    console.error(`Failed to upload ${stage.value} record photos.`, error)
    if (!error.status || error.status >= 500) {
      photoUploadNeedsReview.value = true
    }
    throw error
  }
  savedPhotos.value.push(...photos)
  pendingFiles.value = []
}

async function savePickupRecordAsync (signing = null) {
  isSaving.value = true
  try {
    const isNewRecord = !recordId.value
    const record = recordId.value
      ? await apiClient.postAsync(`/api/drivepark/records/${recordId.value}/pickup`, getPickupPayload())
      : await apiClient.postAsync('/api/drivepark/records', {
        ...getPickupPayload(),
        ...(typeof route.query.reservationId === 'string'
          ? { driveParkReservationId: route.query.reservationId }
          : {}),
      })

    recordId.value = record.id

    if (isNewRecord) {
      await router.replace({
        name: 'fleet-record-pickup-form',
        params: { recordId: record.id },
        query: route.query,
      })
    }

    await syncPhotosAsync()

    if (signing) {
      await apiClient.postAsync(`/api/drivepark/records/${recordId.value}/pickup`, {
        ...getPickupPayload(),
        signing,
      })
    }
  } finally {
    isSaving.value = false
  }
}

async function saveReturnRecordAsync (signing = null) {
  isSaving.value = true
  try {
    const path = `/api/drivepark/records/${recordId.value}/return`
    await apiClient.postAsync(path, getReturnPayload())
    await syncPhotosAsync()

    if (signing) {
      await apiClient.postAsync(path, { ...getReturnPayload(), signing })
    }
  } finally {
    isSaving.value = false
  }
}

async function goToReviewAsync () {
  if (isSaving.value || photoUploadNeedsReview.value) return

  const inputError = getInputError()
  if (inputError) {
    showErrorToast(inputError)
    return
  }

  const { valid } = await recordFormRef.value.validate()
  if (!valid) {
    showErrorToast('Ispunite obavezna polja prije potpisa.')
    return
  }

  step.value = 2
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

async function saveDraftAsync () {
  if (isSaving.value || photoUploadNeedsReview.value) return

  const inputError = getInputError()
  if (inputError) {
    showErrorToast(inputError)
    return
  }

  try {
    await (isReturnForm.value ? saveReturnRecordAsync() : savePickupRecordAsync())
    showSuccessToast(isReturnForm.value ? 'Draft zapisnika o vraćanju je uspješno spremljen.' : 'Draft zapisnika je uspješno spremljen.')
    await router.push(returnRoute.value)
  } catch (error) {
    console.error(`Failed to save the ${stage.value} record draft.`, error)
    await showRecordSaveErrorAsync(error, isReturnForm.value ? 'Draft zapisnika o vraćanju nije bilo moguće spremiti.' : 'Draft zapisnika nije bilo moguće spremiti.')
  }
}

async function createRecordAsync () {
  if (isSaving.value || photoUploadNeedsReview.value) return

  if (isReturnForm.value) {
    const inputError = getInputError()
    if (inputError) {
      showErrorToast(inputError)
      return
    }
  }

  termsError.value = termsAccepted.value ? '' : 'Potrebno je prihvatiti opće uvjete.'
  userSignatureError.value = userSignature.value ? '' : 'Potpis korisnika je obavezan.'
  companySignatureError.value = companySignature.value ? '' : 'Potpis Porsche Croatia je obavezan.'

  if (termsError.value || userSignatureError.value || companySignatureError.value) {
    showErrorToast('Prihvatite opće uvjete i dodajte oba potpisa.')
    return
  }

  try {
    const signing = {
      termsAccepted: true,
      userSignature: userSignature.value,
      companySignature: companySignature.value,
    }
    await (isReturnForm.value ? saveReturnRecordAsync(signing) : savePickupRecordAsync(signing))
    showSuccessToast(isReturnForm.value ? 'Zapisnik o vraćanju je uspješno kreiran.' : 'Zapisnik o preuzimanju je uspješno kreiran.')
    await router.push(returnRoute.value)
  } catch (error) {
    console.error(`Failed to complete the ${stage.value} record.`, error)
    await showRecordSaveErrorAsync(error, isReturnForm.value ? 'Zapisnik o vraćanju nije bilo moguće kreirati.' : 'Zapisnik o preuzimanju nije bilo moguće kreirati.')
  }
}

async function showRecordSaveErrorAsync (error, fallbackMessage) {
  if (!recordId.value && error.status === 409) {
    if (error.data?.error === 'reservation_not_found') {
      showErrorToast('Rezervacija više ne postoji.')
    } else if (error.data?.error === 'reservation_has_record') {
      showErrorToast('Za ovu rezervaciju već postoji zapisnik.')
    } else {
      showRequestErrorToast(error, fallbackMessage)
      return
    }

    await router.push({ name: 'fleet-calendar', query: route.query.month ? { month: route.query.month } : {} })
    return
  }

  showRequestErrorToast(error, fallbackMessage)
}

function addSelectedFiles (files) {
  if (savedPhotos.value.length + pendingFiles.value.length + files.length > 20) {
    showErrorToast(isReturnForm.value ? 'Zapisnik može sadržavati najviše 20 fotografija ukupno.' : 'Zapisnik može sadržavati najviše 20 fotografija.')
    return
  }

  if (files.some((file) => !/\.(jpe?g|png|heic)$/i.test(file.name))) {
    showErrorToast('Dopuštene su samo JPG, PNG i HEIC fotografije.')
    return
  }

  if (files.some((file) => file.size > maxPhotoSize)) {
    showErrorToast('Fotografija može imati najviše 10 MB.')
    return
  }

  pendingFiles.value.push(...files)
}

function removePendingFile (index) {
  pendingFiles.value.splice(index, 1)
}

async function downloadPhotoAsync (photo) {
  try {
    const file = await apiClient.getAsync(
      `/api/drivepark/records/${recordId.value}/photos/${photo.id}/download`,
      { responseType: 'blob', headers: { Accept: 'image/jpeg, image/png, image/heic' } }
    )
    const url = URL.createObjectURL(file)
    const link = document.createElement('a')
    link.href = url
    link.download = photo.fileName
    link.click()
    URL.revokeObjectURL(url)
  } catch (error) {
    console.error(`Failed to download a ${stage.value} record photo.`, error)
    showRequestErrorToast(error, 'Fotografiju nije bilo moguće preuzeti.')
  }
}
</script>

<script>
let recordFormSession = null
</script>

<style scoped lang="scss">
.record-form {
  :deep(.page-panel__header) {
    padding-bottom: 16px;
    border-bottom: 1px solid var(--color-border);
  }

  &__terms-link {
    text-decoration: underline;
  }

  :deep(.notice) {
    max-width: none;
  }

  &__signature-label {
    display: block;
    margin-bottom: 8px;
  }

  &__signature-pad {
    height: 160px;
    border: 2px dashed #cbd5e1;
    border-radius: 10px;
    background: #fafafa;
  }

  &__signature-actions {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-top: 8px;
  }

  &__error {
    color: var(--color-danger);
  }

  &__footer-note {
    margin-left: auto;
    font-size: 14px;
    font-style: italic;
    font-weight: 400;
    line-height: 21px;
    text-align: right;
  }
}
</style>
